import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import cn from 'classnames'
import { Portal } from 'react-portal'
import { ReactComponent as CloseSvg } from 'images/icons/close_icon.svg'
import { useSelector } from 'react-redux'
import { RootState } from 'application/core/domain/store/reduxStore'
import { useNavigate } from 'react-router-dom'
import { useUser } from 'application/core/domain/useCase/user/getUser'
import { AuthContext } from 'application/presentation/context/Authorization'
import { reachGoalPartnersClick } from 'application/core/utils/metrika/yandexReachGoals'
import { ReactComponent as CarSvg } from 'images/icons/car_wash.svg'

import { Button } from '../uiComponents'

import s from './Menu.module.scss'

type Props = {
  opened: boolean
  setOpened: Function
}

const Menu = ({ opened, setOpened }: Props) => {
  const navigate = useNavigate()
  const authContext = useContext(AuthContext)
  const { token, currentCar } = useSelector(({ userReducer, carsReducer }: RootState) => ({
    token: userReducer.token,
    currentCar: carsReducer.currentCar,
  }))
  const { data: userData, isLoading } = useUser(token)

  const freeCup = userData && (userData.free_cup || userData.free_cup === 0) ? userData.free_cup : 5

  const closeMenu = useCallback(() => setOpened(false), [setOpened])

  const showOutletSelector = useMemo(
    () =>
      //@ts-ignore
      window.ENV && window.ENV.outletId ? false : true,
    [],
  )

  const goTo = useCallback(
    (to: string) => {
      setOpened(false)
      if (to === '/sign_wash') {
        reachGoalPartnersClick()
      }
      navigate(to)
    },
    [navigate, setOpened],
  )

  const logout = useCallback(() => {
    authContext.logout(() => {
      navigate('/booking')
    })
    closeMenu()
  }, [authContext, closeMenu, navigate])

  useEffect(() => {
    if (opened) {
      document.body.classList.add('menu_opened')
    } else {
      document.body.classList.remove('menu_opened')
    }
    return () => document.body.classList.remove('menu_opened')
  }, [opened])

  return (
    <Portal>
      <div className={cn({ [s.container]: true, [s.opened]: opened })}>
        <div className={s.close} onClick={closeMenu}>
          <CloseSvg />
        </div>
        <div className={s.inner_container}>
          <div className={s.menu_list}>
            {userData ? (
              <>
                <div className={s.user_block} onClick={() => goTo('/profile')}>
                  <div className={s.image}>
                    <div>{userData.name[0].toUpperCase()}</div>
                  </div>
                  <div className={s.info}>
                    <div className={s.title}>{userData.name}</div>
                    <div className={s.phone}>{userData.phone}</div>
                  </div>
                </div>
                <div className={s.free_item} onClick={() => goTo('/free_wash')}>
                  <CarSvg />
                  <div className={s.free_item_txt}>
                    {freeCup === 0
                      ? 'Следующая мойка бесплатная!'
                      : `Осталось ${freeCup} мойки до бесплатной!`}{' '}
                  </div>
                </div>
                <div className={s.list_item} onClick={() => goTo('/cars')}>
                  <div className={s.title}>Автомобиль</div>
                  <div className={cn(s.subtitle, s.gray)}>
                    {currentCar ? (
                      <>
                        {currentCar.number} {currentCar.regionNumber}{' '}
                        <b>
                          {currentCar.name} {currentCar.modelName}
                        </b>
                      </>
                    ) : (
                      'Не выбран'
                    )}
                  </div>
                </div>
                <div className={s.item} onClick={() => goTo('/payment_systems')}>
                  Способы оплаты
                </div>
                <div className={s.item} onClick={() => goTo('/orders')}>
                  История моек
                </div>
              </>
            ) : (
              <Button
                color="fiol"
                text="Войти"
                onClick={() => goTo('/authorization')}
                containerClassName={s.auth_btn}
              />
            )}
            {showOutletSelector && (
              <div className={s.item} onClick={() => goTo('/outlets')}>
                Каталог моек
              </div>
            )}

            {/* <div className={s.item} onClick={() => goTo('/support')}>
              Служба поддержки
            </div> */}
            <div className={s.item} onClick={() => goTo('/care')}>
              Служба заботы
            </div>
            <div className={s.item} onClick={() => goTo('/information')}>
              Информация
            </div>
            <div className={cn(s.item, s.sign_wash)} onClick={() => goTo('/sign_wash')}>
              ⚡️ Подключить автомойку
            </div>
            {userData ? (
              <div className={s.item} onClick={logout}>
                Выйти
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    </Portal>
  )
}

export default React.memo(Menu)
